import { Component, Inject } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from "@modules/alert";
import {
  AiEntityType,
  AiTokensApiService,
} from "../../services/ai-tokens-api.service";
import { AiQuota, AiQuotaPeriod } from "../../models/ai-quota.model";

export interface AiQuotaDialogData {
  mode: "create" | "edit";
  entityType: AiEntityType;
  entityId: string;
  quota?: AiQuota;
}

@Component({
  selector: "app-ai-quota-form-dialog",
  templateUrl: "./ai-quota-form-dialog.component.html",
  styleUrls: ["./ai-quota-form-dialog.component.scss"],
})
export class AiQuotaFormDialogComponent {
  periods: AiQuotaPeriod[] = ["day", "week", "month"];
  saving = false;

  form = this.fb.group({
    period: ["month" as AiQuotaPeriod, Validators.required],
    maxCostDollars: [
      null as number | null,
      [Validators.required, Validators.min(0.01)],
    ],
  });

  constructor(
    private fb: FormBuilder,
    private api: AiTokensApiService,
    private alertService: AlertService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<AiQuotaFormDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: AiQuotaDialogData
  ) {
    if (data.mode === "edit" && data.quota) {
      this.form.patchValue({
        period: data.quota.period,
        maxCostDollars: data.quota.maxCostCents / 100,
      });
    }
  }

  get isEdit(): boolean {
    return this.data.mode === "edit";
  }

  periodLabelKey(period: AiQuotaPeriod): string {
    switch (period) {
      case "day":
        return "AI_TOKENS.DAY";
      case "week":
        return "AI_TOKENS.WEEK";
      case "month":
        return "AI_TOKENS.MONTH";
      default:
        return "AI_TOKENS.MONTH";
    }
  }

  submit(): void {
    if (this.form.invalid) return;
    const raw = this.form.getRawValue();
    const payload = {
      period: raw.period as AiQuotaPeriod,
      maxCostCents: Math.round((raw.maxCostDollars ?? 0) * 100),
    };
    this.saving = true;

    const handleSuccess = (): void => {
      this.saving = false;
      this.alertService.success(
        this.translate.instant(
          this.isEdit ? "AI_TOKENS.QUOTA_UPDATED" : "AI_TOKENS.QUOTA_CREATED"
        )
      );
      this.dialogRef.close(true);
    };
    const handleError = (): void => {
      this.saving = false;
      this.alertService.error(
        this.translate.instant("AI_TOKENS.QUOTA_SAVE_FAILED")
      );
    };

    if (this.isEdit && this.data.quota) {
      this.api
        .updateQuota(
          this.data.entityType,
          this.data.entityId,
          this.data.quota.id,
          payload
        )
        .subscribe({ next: handleSuccess, error: handleError });
    } else {
      this.api
        .createQuota(this.data.entityType, this.data.entityId, payload)
        .subscribe({ next: handleSuccess, error: handleError });
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
